import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi, ProgramState, ProgramStatePatch } from '../../api/platform';
import { SCHOOL_TYPES } from '../../api/admin';

/**
 * The platform-wide state roster, edited by SGOKit staff: opted_in and
 * eligible_school_types. Every tenant reads these live through its StateConfig FK
 * (nothing to fan out), so a change here applies to every organization. Tenants
 * see both read-only on their States page.
 */
const ProgramStates: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: states, isLoading } = useQuery<ProgramState[]>({
    queryKey: ['program-states'],
    queryFn: platformApi.listProgramStates,
  });

  const mutation = useMutation({
    mutationFn: ({ code, patch }: { code: string; patch: ProgramStatePatch }) =>
      platformApi.updateProgramState(code, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData<ProgramState[]>(['program-states'], (prev) =>
        (prev ?? []).map((s) =>
          s.abbreviated_name === updated.abbreviated_name ? updated : s,
        ),
      );
    },
  });

  if (isLoading || !states) {
    return <p className="sgk-admin__sub" data-testid="program-states-loading">Loading…</p>;
  }

  const optedInCount = states.filter((s) => s.opted_in).length;

  return (
    <>
      <h1 className="sgk-admin__title">Program states</h1>
      <p className="sgk-admin__sub">
        Which states have opted into the SGO scholarship program. This is platform
        policy: a change here applies to every organization. {optedInCount} of{' '}
        {states.length} opted in.
      </p>
      <div className="sgk-table-wrap">
        <div className="sgk-table-scroll">
          <table className="sgk-table">
            <thead>
              <tr>
                <th>State</th>
                <th>Opted in</th>
                <th>Eligible school types</th>
              </tr>
            </thead>
            <tbody>
              {states.map((state) => (
                <tr
                  key={state.abbreviated_name}
                  className={`sgk-srow ${state.opted_in ? 'is-opted' : 'is-out'}`}
                  data-testid={`program-state-row-${state.abbreviated_name}`}
                >
                  <td className="sgk-table__state">
                    {state.full_name}{' '}
                    <span className="sgk-table__abbr">{state.abbreviated_name}</span>
                  </td>
                  <td>
                    <label className="sgk-toggle">
                      <input
                        type="checkbox"
                        checked={state.opted_in}
                        onChange={(e) =>
                          mutation.mutate({
                            code: state.abbreviated_name,
                            patch: { opted_in: e.target.checked },
                          })
                        }
                        data-testid={`superuser-program-state-opted-in-checkbox-${state.abbreviated_name}`}
                      />
                      <span className="sgk-toggle__track" />
                    </label>
                  </td>
                  <td>
                    <div className="sgk-checkset">
                      {SCHOOL_TYPES.map((type) => (
                        <label key={type.value} className="sgk-check">
                          <input
                            type="checkbox"
                            checked={state.eligible_school_types.includes(type.value)}
                            onChange={() =>
                              mutation.mutate({
                                code: state.abbreviated_name,
                                patch: {
                                  eligible_school_types: state.eligible_school_types.includes(type.value)
                                    ? state.eligible_school_types.filter((v) => v !== type.value)
                                    : [...state.eligible_school_types, type.value],
                                },
                              })
                            }
                            data-testid={`superuser-program-state-school-type-${state.abbreviated_name}-${type.value}`}
                          />
                          {type.label}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ProgramStates;
